/**
 * SEO Component
 * Manages meta tags, Open Graph, Twitter Cards, and JSON-LD structured data
 * Following 2025 SEO best practices
 */

import { Helmet } from 'react-helmet-async'

interface SEOProps {
  title: string
  description: string
  canonical?: string
  type?: 'website' | 'article'
  image?: string
  jsonLd?: object
  keywords?: string
}

const SITE_NAME = 'Bitcoin.com Loans & Mortgages'
const SITE_URL = 'https://loans.bitcoin.com'
const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`
const TWITTER_HANDLE = '@BitcoinCom'

export function SEO({
  title,
  description,
  canonical,
  type = 'website',
  image = DEFAULT_IMAGE,
  jsonLd,
  keywords,
}: SEOProps) {
  const fullTitle = `${title} | ${SITE_NAME}`
  const url = canonical ? `${SITE_URL}${canonical}` : SITE_URL

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:creator" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={title} />

      {/* Keywords */}
      {keywords && <meta name="keywords" content={keywords} />}

      {/* Additional SEO Tags */}
      <meta name="robots" content="index, follow" />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
      <meta name="author" content="Bitcoin.com" />

      {/* JSON-LD Structured Data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  )
}

/**
 * Default Organization Schema for Bitcoin.com
 */
export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'FinancialService',
  name: 'Bitcoin.com Loans & Mortgages',
  description: 'Borrow against your crypto. Keep your Bitcoin, access cash for cars, homes, and more.',
  url: SITE_URL,
  logo: `${SITE_URL}/logo.png`,
  sameAs: [
    'https://twitter.com/BitcoinCom',
    'https://www.facebook.com/Bitcoincom',
    'https://www.linkedin.com/company/bitcoin-com',
    'https://www.youtube.com/c/BitcoincomOfficial',
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'Customer Support',
    url: `${SITE_URL}/about`,
  },
}

/**
 * Website Schema
 */
export const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Bitcoin.com Loans & Mortgages',
  url: SITE_URL,
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${SITE_URL}/search?q={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
}

/**
 * Financial Product Schema
 */
export const financialProductSchema = {
  '@context': 'https://schema.org',
  '@type': 'FinancialProduct',
  name: 'Crypto-Backed Loans',
  description: 'Secure loans backed by your cryptocurrency holdings. Borrow against Bitcoin, Ethereum, and other digital assets.',
  provider: {
    '@type': 'FinancialService',
    name: 'Bitcoin.com Loans & Mortgages',
    url: SITE_URL,
  },
  category: 'Loan',
  feesAndCommissionsSpecification: 'Competitive interest rates with no prepayment penalties',
  interestRate: {
    '@type': 'QuantitativeValue',
    value: 'Variable',
    unitText: 'Percentage per annum',
  },
}

/**
 * Breadcrumb Schema Generator
 */
export function generateBreadcrumbSchema(items: { name: string; url: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${SITE_URL}${item.url}`,
    })),
  }
}
